import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ResultConfiguracion } from './configurar-grilla.component';
import { Router, NavigationEnd } from '@angular/router'; 
import { filter } from 'rxjs/operators';

interface StoredData {
    [key: string]: {
        [key: string]: ResultConfiguracion; 
    };
}

@Injectable({
    providedIn: 'root'
})
export class ConfigurarGrillaService {
    private checkboxesSubject = new BehaviorSubject<ResultConfiguracion | null>(null);
    checkboxes$ = this.checkboxesSubject.asObservable();

    constructor(private router: Router) {
        this.router.events.pipe(
            filter(event => event instanceof NavigationEnd)
        ).subscribe(() => {
            this.loadFromSessionStorage();
        });

        this.loadFromSessionStorage(); 
    }

    /**
     * Actualiza la configuración de los checkboxes y la guarda en el sessionStorage
     * Este método emite la nueva configuración a través de 'checkboxesSubject' y
     * lo almacena utilizando 'saveToSessionStorage'. 
     * @param resultConfiguracion - La nueva configuración de los checkboxes a actualizar.
     */
    updateCheckboxes(resultConfiguracion: ResultConfiguracion) {
        this.checkboxesSubject.next(resultConfiguracion);
        this.saveToSessionStorage(resultConfiguracion);
    }

    /**
     * Se guarda la configuración de los checkboxes en el sessionStorage
     * Para eso se almacena 'resultConfiguracion' bajo la llave 'checkboxesData', creando una nueva entrada
     * si no exite para la ruta especificada. 
     * @param resultConfiguracion - Configuración a almacenar, que incluye 
     * la ruta, un identificador único y los checkboxes. 
     */
    private saveToSessionStorage(resultConfiguracion: ResultConfiguracion) {
        const existingData: StoredData = JSON.parse(sessionStorage.getItem('checkboxesData') || '{}');
        
        if (!existingData[resultConfiguracion.ruta]) {
            existingData[resultConfiguracion.ruta] = {};
        }
        
        existingData[resultConfiguracion.ruta][resultConfiguracion.identificador] = resultConfiguracion;
        sessionStorage.setItem('checkboxesData', JSON.stringify(existingData));
    }

    /**
     * Método para obtener la Configuración de los checkboxes desde la sessionStorage
     * 
     * La funcion recupera los datos de 'checkboxesData' y busca la configuración 
     * correspondiente a la ruta actual. Si se encuentra se emite la configuración 
     * a través de 'checkboxesSubject' de lo contrario, emite un valor. 
     */
    private loadFromSessionStorage() {
        const storedData: StoredData = JSON.parse(sessionStorage.getItem('checkboxesData') || '{}');
        
        // Se almacena la ruta actual para luego comparar con la que se almaceno en el sessionStorage
        const currentRoute = this.getCurrentRoute();
        
        // Se busca la configuración para la ruta actual
        const storedConfig = storedData[currentRoute] ? Object.values(storedData[currentRoute]).find(config => config.identificador) : null;

        if (storedConfig) {
            this.checkboxesSubject.next(storedConfig);
        } else {
            // Si no hay configuración almacenada, puedes inicializar con valores por defecto si es necesario
            this.checkboxesSubject.next(null);
        }
    }

    /**
     * Se obtiene la ruta actual de donde se este parado
     * @returns 
     */
    private getCurrentRoute(): string {
        const fullUrl = this.router.url; 
        const segments = fullUrl.split('/').filter(segment => segment); 
        const currentRoute = `/${segments.slice(0, 2).join('/')}`; 
        return currentRoute; 
    }
}